import { useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { setCookie } from 'cookies-next'
import { observer } from 'mobx-react-lite'
import { Progress } from '@mantine/core'
import { Button, showNotification } from '@gate/gui'
import useLangPrefix from '@/hooks/useLangPrefix'
import { useStore } from '@/store/index'
import { baseMarketSKey, stopEvent } from '@/utils/tools'
import { useRequest } from 'ahooks'
import { eventTracker } from '@/utils/collectEvent'
import { setMarketEmotion } from 'api/price'
import { useAdapter } from '@/components/Adapter'
import {
  MarketRateStyled,
  MarketRateTitleStyled,
  MarketRateProgressBoxStyled,
  MarketRateProgressNumberStyled,
  MarketRateButtonStyled,
  ProgressBox,
} from './style'
import BaseTooltip from '@/components/BaseTooltip'

const HeatChart = () => {
  const { size, isH5 } = useAdapter()

  const {
    baseStore,
    globalStore: { commonData, isDark },
  } = useStore()

  const { priceVixData, resMarketEmotion, userMarketState, setMarket } = baseStore

  const { t } = useTranslation()
  const { langPrefix } = useLangPrefix()
  const { locale } = useRouter()
  const [marketState, setMarketState] = useState(userMarketState)

  const { runAsync, loading } = useRequest(setMarketEmotion, {
    manual: true,
  })

  const vixDataLang = useMemo(() => {
    const en = priceVixData?.language?.find(item => item?.i18n_lang === 'en')

    if (locale === 'zh') {
      return priceVixData?.language?.find(item => item?.i18n_lang === 'zh-Hans')?.i18n_value ?? en?.i18n_value
    }

    if (locale === 'zh-tw') {
      return (
        priceVixData?.language?.find(item => item?.i18n_lang === 'zh-Hant')?.i18n_value ??
        priceVixData?.language?.find(item => item?.i18n_lang === 'zh-Hans')?.i18n_value ??
        en?.i18n_value
      )
    }

    if (locale === 'tr') {
      return priceVixData?.language?.find(item => item?.i18n_lang === 'en')?.i18n_value ?? en?.i18n_value
    }

    return priceVixData?.language?.find(item => item?.i18n_lang === locale)?.i18n_value ?? en?.i18n_value
  }, [locale, priceVixData?.language])

  const handleSetMarket = (type: '1' | '0') => {
    if (!commonData?.is_login) {
      showNotification({ message: t('P.PLF'), type: 'warning' })
      return
    }

    if (marketState) {
      return
    }
    runAsync(type === '0' ? 'bad' : 'good').then(res => {
      if (!res.expire_time) {
        showNotification({ message: t('P.PLF'), type: 'warning' })
        return
      } else {
        setMarket(res)
        setMarketState(type)
        setCookie(baseMarketSKey, type, {
          expires: new Date(Date.now() + Number(res.expire_time) * 1000),
          path: '/',
          secure: true,
        })
      }
    })
  }

  const buttonSize = useMemo(() => {
    if (size === 'md') {
      return 'xs'
    }
    return 'sm'
  }, [size])

  return (
    <MarketRateStyled isDark={isDark}>
      <MarketRateTitleStyled onClick={() => window.open(`${langPrefix}/bigdata`)}>
        <BaseTooltip onClick={stopEvent} className="size-20-16 font-700 line-120" component="h2" label={t('P.FAG')}>
          {t('P.FAG')}
        </BaseTooltip>
        <p className="size-14 font-400">
          <strong>{priceVixData?.data?.vix_value ?? '--'}</strong>&nbsp;(
          {vixDataLang})
        </p>
      </MarketRateTitleStyled>
      <MarketRateProgressBoxStyled>
        <BaseTooltip component="p" label={t('P.HDYFATM')}>
          {t(`P.HDYFATM`)}
        </BaseTooltip>

        <ProgressBox>
          <Progress
            size={isH5 ? 12 : 8}
            value={100}
            color="#f2495e"
            w={'100%'}
            sections={[
              { value: Number(resMarketEmotion?.good ?? 50), color: 'var(--color-funct-2)' },
              {
                value: Number(resMarketEmotion?.bad ?? 50),
                color: 'linear-gradient(270deg, #f2495e 0%, #f2495e 154.07%)',
              },
            ]}
          />

          <MarketRateProgressNumberStyled>
            <span>
              {/* 看好 */}
              {marketState === '1' ? (
                <span className="text-funct-2 size-14 font-500">
                  <svg className="iconpark-icon">
                    <use href="#v31-12px-fill-dianzan"></use>
                  </svg>
                </span>
              ) : (
                // 未选择
                <span className="text-1 size-14 font-500">
                  <svg className="iconpark-icon">
                    <use href="#v31-12px-line-dianzan"></use>
                  </svg>
                </span>
              )}
              <span className="text-1 size-14 font-500">{resMarketEmotion?.good ?? 50}%</span>
            </span>

            <span>
              {/* 不看好 */}
              {marketState === '0' ? (
                <span className="size-14 font-500" style={{ color: 'var(--color-pd-1)' }}>
                  <svg className="iconpark-icon">
                    <use href="#v31-12px-fill-weijiejue"></use>
                  </svg>
                </span>
              ) : (
                // 未选择
                <span className="text-1 size-14 font-500">
                  <svg className="iconpark-icon">
                    <use href="#v31-12px-line-weijiejue"></use>
                  </svg>
                </span>
              )}
              <span className="text-1 size-14 font-500">{resMarketEmotion?.bad ?? 50}%</span>
            </span>
          </MarketRateProgressNumberStyled>
        </ProgressBox>
      </MarketRateProgressBoxStyled>

      <MarketRateButtonStyled>
        <Button
          leftIcon={
            <svg className="iconpark-icon">
              <use href="#v31-12px-line-dianzan"></use>
            </svg>
          }
          fullWidth
          disabled={marketState === '0' || marketState === '1'}
          type="outlined"
          size={buttonSize}
          label={`${t('P.Positive')}`}
          loading={loading}
          onClick={() => {
            eventTracker('price_overview_click', {
              button_name: 'positive',
            })
            handleSetMarket('1')
          }}
          styles={{
            label: {
              fontSize: locale === 'ru' ? '12px' : '14px',
            },
          }}
        />
        <Button
          styles={{
            label: {
              fontSize: locale === 'ru' ? '12px' : '14px',
            },
          }}
          leftIcon={
            <svg className="iconpark-icon">
              <use href="#v31-12px-line-weijiejue"></use>
            </svg>
          }
          loading={loading}
          fullWidth
          disabled={marketState === '0' || marketState === '1'}
          type="outlined"
          size={buttonSize}
          label={`${t('P.Negative')}`}
          onClick={() => {
            eventTracker('price_overview_click', {
              button_name: 'negative',
            })
            handleSetMarket('0')
          }}
        />
      </MarketRateButtonStyled>
    </MarketRateStyled>
  )
}

export default observer(HeatChart)
